import React, { useState, useCallback } from 'react';
import pick from 'lodash/pick';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';

// Types
import { GoalItem } from 'Shared/constants/types';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { getActionItems } from 'Shared/selectors/meetingItems';
import PageContext from 'Shared/contexts/PageContext';
import { pageContext } from 'Shared/constants/constants';
import { getSortedItems } from 'Shared/utils/sorting';
import { addItem } from 'Shared/actions/todos';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';

import GoalDetailsPageActionItemsList from './GoalDetailsPageActionItemsList/GoalDetailsPageActionItemsList';
import GoalDetailsPageAiSuggestedSubGoalsOrActionItems from './GoalDetailsPageAiSuggestedSubGoalsOrActionItems/GoalDetailsPageAiSuggestedSubGoalsOrActionItems';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { WpContainer, WpContainerHeader } from 'Wpds/components/container';

import './GoalActionItems.scss';
import GoalProgress from '../GoalProgress/GoalProgress';

type Props = {
  goal: GoalItem;
  showProgress?: boolean;
  forceShowMobileActions?: boolean;
  showTableHeaders?: boolean;
};

const ItemWhiteList = Object.freeze([
  'id',
  'uuid',
  'completed',
  'parentUuid',
  'workspacePath',
  'parentText',
  'isHighPriority',
  'workspaceId',
  'assignmentAvatar',
  'positionForGoalDetailPage',
]);

const sortingMap = {
  actionItems: 'default',
  owner: 'default',
  dueDate: 'default',
};

const GoalActionItems = ({ goal, showProgress = true, forceShowMobileActions = false, showTableHeaders }: Props) => {
  const goalSlug = goal.slug;
  const workspaceSlug = goal.workspaceSlug;
  const { isComplete, isReadOnly, isProgressActionItems } = useGoal({ slug: goal.slug });
  const dispatch = useDispatch();
  const [selectedSort, setSelectedSort] = useState(sortingMap);
  const actionItems = useDeepEqualSelector(state => {
    const items = getActionItems(state).filter(({ goalSlug: meetingItemGoalSlug }) => meetingItemGoalSlug === goalSlug);
    const sortedItems = getSortedItems({
      items,
      selectedSort,
      withHighPriority: true,
      positionField: 'positionForGoalDetailPage',
    });
    return sortedItems.map(actionItem => pick(actionItem, ItemWhiteList));
  });
  const actionItemsLength = actionItems.length;
  const actionItemsPresent = Boolean(actionItemsLength);

  const handleSelectedItemUpdate = useCallback(
    itemUuid => dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true })),
    [dispatch]
  );

  const handleAddItemClick = useCallback(() => {
    const itemUuid = uuidv4();
    const positionForGoalDetailPage = actionItemsLength + 1;
    dispatch(addItem({ itemUuid, goalSlug, positionForGoalDetailPage, workspaceId: workspaceSlug }));
    handleSelectedItemUpdate(itemUuid);
  }, [dispatch, actionItemsLength, goalSlug, workspaceSlug, handleSelectedItemUpdate]);

  const handleBulkAddItems = useCallback(
    bodies => {
      let lastItemUuid;
      bodies.forEach((body, bodyIndex) => {
        const itemUuid = uuidv4();
        const positionForGoalDetailPage = actionItemsLength + 1 + bodyIndex;
        dispatch(addItem({ itemUuid, body, goalSlug, positionForGoalDetailPage, workspaceId: workspaceSlug }));
        lastItemUuid = itemUuid;
      });
      if (lastItemUuid) handleSelectedItemUpdate(lastItemUuid);
    },
    [dispatch, actionItemsLength, goalSlug, workspaceSlug, handleSelectedItemUpdate]
  );

  if (isReadOnly && !actionItemsPresent) return null;

  const baseClassName = 'GoalActionItems';

  return (
    <PageContext.Provider value={pageContext.GOAL_DETAIL_PAGE}>
      <WpContainer className={baseClassName}>
        {isProgressActionItems && actionItemsPresent && showProgress && (
          <WpContainerHeader>
            <p className={`${baseClassName}__progress-header`}>Goal Progress</p>
            <GoalProgress goal={goal} view="page" />
          </WpContainerHeader>
        )}
        {actionItemsPresent ? (
          <GoalDetailsPageActionItemsList
            goalIsReadOnly={isReadOnly}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            goalSlug={goal.slug}
            actionItems={actionItems}
            forceShowMobileActions={forceShowMobileActions}
            showTableHeaders={showTableHeaders}
          />
        ) : null}
        {!actionItemsPresent && <EmptyStateContainer subTitle="There are no action items here yet." />}
      </WpContainer>
      {isComplete || isReadOnly ? null : (
        <div className={`${baseClassName}__buttons`}>
          <WpButtonGroup spacing="md">
            <WpButton variant="tertiary" onClick={handleAddItemClick} size="xs" square slimPadding>
              <WpIcon name="plus" size="sm" />
              Add Action Item
            </WpButton>
            <GoalDetailsPageAiSuggestedSubGoalsOrActionItems
              goalSlug={goalSlug}
              handleBulkAddItems={handleBulkAddItems}
              suggestedObjectType={'action_item'}
            />
          </WpButtonGroup>
        </div>
      )}
    </PageContext.Provider>
  );
};

export default GoalActionItems;
